import { makeStyles, Box, Typography } from '@material-ui/core';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { InfoOutlined } from '@material-ui/icons';
import { CustomIconTooltip } from '@roc/ui';
import { isNil } from '@roc/feature-utils';
import React, { useEffect, useRef, useState } from "react";
import clsx from 'clsx';

export const EARNINGS_PURPLE = '#7B3FA0';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 0,
  },
  alignRight: {
    marginLeft: 'auto',
    textAlign: 'right',
  },
  title: {
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 500,
    color: '#5E69A6',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  alignRightTitle: {
    justifyContent: 'flex-end',
  },
  // amount with its percentage note underneath (keeps the strip narrow enough for one row)
  valueRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  amount: {
    color: EARNINGS_PURPLE,
    fontSize: 24,
    lineHeight: '32px',
    fontWeight: 700,
    display: 'inline-block',
    transition: "color 0.4s ease, transform 0.4s ease",
  },
  totalAmount: {
    fontSize: 28,
    lineHeight: '36px',
  },
  percentage: {
    fontSize: 13,
    color: '#5E69A6',
    whiteSpace: 'nowrap',
  },
  percentageValue: {
    color: EARNINGS_PURPLE,
    fontWeight: 600,
  },
  animate: {
    color: "#5D2C6F",
    transform: "scale(1.1)",
  },
  symbol: {
    flexShrink: 0,
    alignSelf: 'center',
    fontSize: 24,
    fontWeight: 600,
    color: '#B9A3CC',
  },
}));

interface EarningsCardProps {
  title: string;
  earnings: number;
  percentage?: number
  percentageDescription?: string;
  position?: 'left' | 'center' | 'right';
  tooltip?: string;
}


const AnimatedTypography = ({ value, className }) => {
  const classes = useStyles();
  const typographyRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const handleAnimation = () => {
    const element = typographyRef.current;
    if (element) {
      element.classList.add(classes.animate);
      setTimeout(() => {
        element.classList.remove(classes.animate);
      }, 400);
    }
  };

  useEffect(() => {
    if (!isFirstRender && value) {
      handleAnimation();
    } else {
      setIsFirstRender(false);
    }
  }, [value]);

  return (
    <Typography
      ref={typographyRef}
      component="span"
      className={className}
    >
      {value}
    </Typography>
  );
};


/** One figure in the earnings strip: small label, amount, and an optional percentage note. */
export const EarningsCard = ({
  title,
  earnings,
  percentage,
  percentageDescription,
  position = 'center',
  tooltip,
}: EarningsCardProps) => {
  const classes = useStyles();
  // the right-hand figure is the total: pushed to the end of the strip and slightly larger
  const isTotal = position === 'right';

  return (
    <Box className={clsx(classes.root, isTotal && classes.alignRight)}>
      <Box className={clsx(classes.title, isTotal && classes.alignRightTitle)}>
        {title}
        {tooltip && (
          <CustomIconTooltip
            style={{ fontSize: 16 }}
            placement="top"
            title={tooltip}
            icon={InfoOutlined}
          />
        )}
      </Box>
      <Box className={classes.valueRow} alignItems={isTotal ? 'flex-end' : 'flex-start'}>
        <AnimatedTypography
          value={!isNil(earnings) ? formatCurrency(earnings) : '-'}
          className={clsx(classes.amount, isTotal && classes.totalAmount)}
        />
        {!isNil(percentage) && (
          <span className={classes.percentage}>
            <span className={classes.percentageValue}>{formatPercentage(percentage)}</span>{' '}
            {percentageDescription}
          </span>
        )}
      </Box>
    </Box>
  );
};

export const EarningsCardSymbol = ({
  text,
}: {
  text: string
}) => {
  const classes = useStyles();
  return <span className={classes.symbol}>{text}</span>;
};
